import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

import ChainageCard from "./UtilityCard";
import AddChainageModal from "./AddChainageModal";
import { Chainage, Crossing } from "./types";
import UtilityCard from "./UtilityCard";

interface Props<T> {
  mode: "chainage" | "crossing";
  chainages: T[];
  setChainages: React.Dispatch<React.SetStateAction<T[]>>;
  formik?: any;
}

const ChainageSection = <T extends Chainage | Crossing>({
  mode,
  chainages,
  setChainages,
  formik,
}: Props<T>) => {
  const [open, setOpen] = useState(false);

  const [editing, setEditing] = useState<T | null>(null);

  const handleOpen = () => {
    setEditing(null);
    setOpen(true);
  };

  const handleClose = () => {
    setEditing(null);
    setOpen(false);
  };

  const handleEdit = (item: T) => {
    setEditing({ ...item });
    setOpen(true);
  };

  const handleDelete = (id: number) => {
    setChainages((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = (value: T) => {
    if (editing) {
      setChainages((prev) =>
        prev.map((item) => (item.id === value.id ? value : item)),
      );
    } else {
      setChainages((prev) => [...prev, value]);
    }

    setEditing(null);
    setOpen(false);
  };

  const formatChainage = (km: string | number, m: string | number) =>
    (Number(km) + Number(m) / 1000).toFixed(2);

  return (
    <Box>
      {chainages.length > 0 && (
        <Box display="flex" flexDirection="column">
          {chainages.map((item, index) => (
            <UtilityCard
              key={item.id}
              title={`${mode === "chainage" ? "Chainage" : "Crossing"} ${index + 1}`}
              subtitle={
                mode === "chainage"
                  ? `(${formatChainage(
                      (item as Chainage).startChainageKm,
                      (item as Chainage).startChainageM,
                    )} to ${formatChainage(
                      (item as Chainage).endChainageKm,
                      (item as Chainage).endChainageM,
                    )})`
                  : `(${formatChainage(
                      (item as Crossing).crossingChainageKm,
                      (item as Crossing).crossingChainageM,
                    )})`
              }
              onEdit={() => handleEdit(item)}
              onDelete={() => handleDelete(item.id)}
            />
          ))}
        </Box>
      )}

      {/* Add Chainage Button */}

      <Button
        startIcon={<AddIcon />}
        onClick={handleOpen}
        disableRipple
        sx={{
          p: 0,
          mt: 1,
          minWidth: "auto",
          color: "#3A1C73",
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 500,
          justifyContent: "flex-start",

          "&:hover": {
            background: "transparent",
          },

          "& .MuiButton-startIcon": {
            marginRight: "6px",
          },

          "& .MuiSvgIcon-root": {
            fontSize: 22,
          },
        }}
      >
        {chainages.length === 0 ? (
          <Typography>
            Add Chainage<span className="required"> *</span>
          </Typography>
        ) : (
          <Typography>Add More Chainage</Typography>
        )}
      </Button>

      {/* Validation */}

      {formik && formik.submitCount > 0 && chainages.length === 0 && (
        <Typography
          color="error"
          sx={{
            mt: 1,
            fontSize: 13,
          }}
        >
          Please add at least one chainage.
        </Typography>
      )}

      {/* Modal */}

      <AddChainageModal
        mode={mode}
        open={open}
        editing={editing}
        onClose={handleClose}
        onSave={handleSave as (value: Chainage | Crossing) => void}
      />
    </Box>
  );
};

export default ChainageSection;
